import type {
    IKeywordSearchEngine,
    IParsedFileData,
} from "app/pages/digital-marketing/brand-monitoring-campaigns/wizard/wizard-pages/adding-keywords-step/importCSVModal/importCSVModalConsts";
import type { IKeywords } from "app/pages/digital-marketing/brand-monitoring-campaigns/wizard/wizard-pages/adding-keywords-step/keywords-table/BrandMonitoringCampaignTableConsts";
import { translate } from "app/filters/i18nFilter";
import {
    ADD_MANUALLY_DUPLICATE_KEYWORD_KEY,
    ADD_MANUALLY_DUPLICATE_KEYWORDS_KEY,
} from "app/pages/digital-marketing/brand-monitoring-campaigns/wizard/wizard-pages/adding-keywords-step/keywords-table/table-top/BrandMonitoringCapmaignTableTopConsts";
import type { ISearchEnginesKeywordsProps } from "app/components/React/Table/cells/SearchEnginesKeywordCell/SearchEnginesKeywordCellConst";
const MAX_KEYWORDS_AMOUNT_ERROR_KEY =
    "paid.brandMonitoringCampaign.wizard.addingKeywordsStep.addCSV.error.maxAmountOfKeywordsExceeded";

const MAX_CHARACTERS_PER_PHRASE = 500;

const validatePhrase = (phrase: string): boolean => {
    return !!phrase.trim() && phrase.length <= MAX_CHARACTERS_PER_PHRASE;
};

export const getUpdatedKeywordsWithoutDuplications = (
    data: IKeywordSearchEngine[],
    wizardStateKeywords: IKeywords[],
    amountOfKeywordsRemaining: number,
    includeExistingKeywordsInLimitCheck = true,
    availableSearchEngines?,
): IParsedFileData => {
    const existsKeywords = wizardStateKeywords ?? [];
    let amountOfNewKeywords = 0;
    let amountOfDuplicatedKeywords = 0;
    let error;
    const dataWithoutDuplications: IKeywords[] = data.reduce(
        (acc, current) => {
            const isKeywordValid = validatePhrase(current.keyword);
            if (isKeywordValid && !error) {
                const index = acc.findIndex((item) => item.keyword.toLowerCase() === current.keyword.toLowerCase());
                let currentCount = amountOfNewKeywords;
                // This logic is only applicable for the wizard when the list starts with 0 entries.
                // In the edit page, the list has an initial size of the amount of keywords in the campaign
                // and is already taken into account in the "amountOfKeywordsRemaining" prop
                if (includeExistingKeywordsInLimitCheck) {
                    currentCount += wizardStateKeywords?.length;
                }
                const isMaxLimitReached = currentCount >= amountOfKeywordsRemaining;
                const getKeywordWithSearchEngine = getNewKeyword(current, availableSearchEngines);
                if (index === -1) {
                    if (isMaxLimitReached) {
                        error = MAX_KEYWORDS_AMOUNT_ERROR_KEY;
                        return acc;
                    }
                    amountOfNewKeywords++;
                    return [getKeywordWithSearchEngine].concat(acc);
                } else {
                    amountOfDuplicatedKeywords++;
                    acc[index] = {
                        keyword: acc[index].keyword,
                        searchEngine: acc[index].searchEngine,
                    };
                }
            }
            return acc;
        },
        [
            ...existsKeywords.map((item) => {
                return {
                    keyword: item.keyword,
                    searchEngine: item.searchEngine,
                };
            }),
        ],
    );
    return {
        dataWithoutDuplications,
        amountOfNewKeywords,
        amountOfDuplicatedKeywords,
        error,
    };
};

const handleDuplicateKeywords = (keywordsAmount): string => {
    return translate(keywordsAmount === 1 ? ADD_MANUALLY_DUPLICATE_KEYWORD_KEY : ADD_MANUALLY_DUPLICATE_KEYWORDS_KEY, {
        keywordsAmount: keywordsAmount,
    });
};

const addNewKeywords = (keywordsAmount): string => {
    return translate("campaigns.addingKeywords.manual.toast.successMessage.noDuplicates", {
        count: keywordsAmount,
    });
};
export const getToastMessage = (keywordsData: IParsedFileData): string[] => {
    const toastMessages = [];
    if (keywordsData.amountOfNewKeywords > 0) {
        toastMessages.push(addNewKeywords(keywordsData.amountOfNewKeywords));
    }

    if (keywordsData.amountOfDuplicatedKeywords > 0) {
        toastMessages.push(handleDuplicateKeywords(keywordsData.amountOfDuplicatedKeywords));
    }
    return toastMessages;
};

const getNewKeyword = (currentItem, availableSearchEngines: ISearchEnginesKeywordsProps[]): IKeywords => {
    const doesSearchEngineExist = currentItem.searchEngine;
    return {
        keyword: currentItem.keyword.toLowerCase(),
        searchEngine: doesSearchEngineExist ?? availableSearchEngines,
    };
};
